import { FieldArray, FormikHelpers } from 'formik'
import React, { FC, useEffect, useMemo } from 'react'
import Form from '../../components/Form'
import CustomButton from '../../components/UI/Buttons/CustomButton'
import TextInput from '../../components/UI/Inputs/TextInput'
import { useDispatch, useSelector } from '../../store'
import { inviteToProjectThunk } from '../../store/project/thunks'
import SelectInput from '../../components/UI/Inputs/SelectInput'
import { push } from 'connected-react-router'
import MultiselectInput from '../../components/UI/Inputs/MultiselectInput'
import { isFulfilled } from '@reduxjs/toolkit'
import { NON_AUTH_ROUTE } from '../App/routes'
import { getAgencyInvitationsThunk } from '../../store/agency/thunks'
import Roles from '../../models/roles'
import TrashIcon from '../../components/UI/Icons/Trash'

const transformRoleName = (role: Roles) => {
  const displayRole = role.replaceAll('-', ' ').split('')
  displayRole.splice(0, 1, displayRole[0].toLocaleUpperCase())
  return displayRole.join('')
}

interface CreateProjectStep2Props {
  goForward: (props?: any) => void
  skipCreation?: () => void
  inProgress: boolean
  project_id: any
}

const CreateProjectStep2: FC<CreateProjectStep2Props> = ({
  inProgress,
  project_id,
  goForward,
  ...props
}) => {
  const dispatch = useDispatch()
  const { agencyId, agencyUsers, location, agencyRoles } = useSelector((state) => ({
    agencyId: state.agency.agency.id,
    agencyUsers: state.agency.agencyUsers,
    location: state.router.location,
    agencyRoles: state.agency.roles
  }))

  useEffect(() => {
    if (agencyId) {
      const getAgencyUsers = async () => {
        await dispatch(getAgencyInvitationsThunk(agencyId))
      }
      getAgencyUsers()
    }
  }, [agencyId])

  const setUpForm = {
    initialValues: {
      invite_to_project: [{ name: '', email: '', project_role: '' }],
      members: []
    },
    validation: 'invite_members',
    validateOnBlur: false,
    onSubmit: async (values: any, actions: FormikHelpers<any>) => {
      const invitePromises = values.invite_to_project.map((member: any) => {
        const data = {
          ...member,
          project_id
        }
        return dispatch(inviteToProjectThunk(data))
      })
      const inviteStatus = await Promise.all(invitePromises)
      // TODO check all invitations
      if (!inviteStatus[0] || isFulfilled(inviteStatus[0] as any)) {
        goForward()
      }
    }
  }

  const agencyMembersList = useMemo(
    () =>
      agencyUsers
        .filter((user) => user.role !== Roles.EXTERNAL_APPROVER)
        .map((user) => {
          return {
            member: {
              id: user.id,
              name: user.name,
              role: user.role,
              displayRole: transformRoleName(user.role),
              email: user.email
            },
            value: user.id,
            label: user.name
          }
        }),
    [agencyUsers]
  )

  useEffect(() => {
    if (!project_id) {
      dispatch(push(NON_AUTH_ROUTE.LOGIN))
    }
  }, [location, dispatch, project_id])

  return (
    <div className='agency-creation-step project-creation step-2'>
      <div className='agency-creation-step__title'>Invite members to your project</div>
      <div className='agency-creation-step__description'>Choose existing members</div>
      <Form {...setUpForm}>
        <MultiselectInput
          label='Agency role'
          name={`members`}
          placeholder='Choose members'
          options={agencyMembersList}
        />
        <div className='agency-creation-step__invite-rows'>
          <FieldArray
            name='invite_to_project'
            render={({ push, remove, form: { values, ...formRest }, ...rest }) => (
              <>
                {values.invite_to_project &&
                  values.invite_to_project.map((user: any, index: number) => (
                    <div key={index} className='agency-creation-step__invite-row'>
                      <TextInput
                        label='Name'
                        name={`invite_to_project.${index}.name`}
                        placeholder='Name'
                      />
                      <TextInput
                        label='Email'
                        name={`invite_to_project.${index}.email`}
                        placeholder='Email'
                      />
                      <SelectInput
                        label='Agency role'
                        name={`invite_to_project.${index}.project_role`}
                        placeholder='Role'
                        options={agencyRoles}
                      />
                      <CustomButton
                        type='button'
                        className='agency-creation-step__invite-row__delete'
                        button='delete'
                        onClick={() => remove(index)}
                      >
                        <TrashIcon />
                      </CustomButton>
                    </div>
                  ))}
                <CustomButton
                  type='button'
                  className='agency-creation-step__button-add'
                  button='subtle'
                  onClick={() => push({ name: '', email: '', project_role: '' })}
                >
                  Add Member
                </CustomButton>
              </>
            )}
          />
        </div>

        <div className='agency-creation-step__buttons'>
          <CustomButton button='primary' size='big' type='button' onClick={goForward}>
            Skip
          </CustomButton>
          <CustomButton inProgress={inProgress} button='primary' size='big' type='submit'>
            Submit
          </CustomButton>
        </div>
      </Form>
    </div>
  )
}

export default CreateProjectStep2
